import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Navigate } from 'react-router-dom';

import { communityReportsApi } from '../../api/services';
import { Skeleton } from '../../components/Feedback/Skeleton';
import { PageTransition } from '../../components/Transitions/PageTransition';
import { ConfidenceBadge } from '../../components/Trust/ConfidenceBadge';
import { StalenessBadge } from '../../components/Trust/StalenessBadge';
import { useAuth } from '../../contexts/AuthContext';
import type { CommunityReportAdmin, ReportModeration, ReportType } from '../../types';

const statuses: ReportModeration[] = ['pending', 'approved', 'rejected', 'hidden'];
const types: Array<ReportType | ''> = ['', 'test_seat_availability', 'embassy_status', 'payment_method'];

/** Moderation queue for community reports. Same pattern as the experience review page. */
export function AdminReportReviewPage() {
  const { user } = useAuth();
  const [items, setItems] = useState<CommunityReportAdmin[]>([]);
  const [status, setStatus] = useState<ReportModeration>('pending');
  const [type, setType] = useState<ReportType | ''>('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);

  useEffect(() => {
    if (!user?.is_admin) return;
    setLoading(true);
    communityReportsApi
      .queue(status, type || undefined)
      .then(setItems)
      .catch((err) => {
        console.error('Failed to load report queue:', err);
        toast.error('Could not load the report queue');
      })
      .finally(() => setLoading(false));
  }, [status, type, user?.is_admin]);

  if (!user?.is_admin) return <Navigate to="/" replace />;

  const moderate = async (id: number, moderation_status: ReportModeration, verified?: boolean) => {
    setBusy(id);
    try {
      await communityReportsApi.moderate(id, { moderation_status, verified });
      setItems((prev) => prev.filter((item) => item.id !== id || moderation_status === status));
      toast.success(verified ? 'Approved and verified' : `Marked ${moderation_status}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update the report');
    } finally {
      setBusy(null);
    }
  };

  return (
    <PageTransition>
      <div className="mx-auto max-w-6xl px-4 py-8">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text-primary">Report review</h1>
            <p className="mt-1 max-w-[65ch] text-sm text-text-secondary">
              Seat reports show as unconfirmed before review; embassy and payment reports stay hidden until approved.
              Notes are only shown after approval. Verify only what you have checked against a reliable source.
            </p>
          </div>
          <div className="flex gap-2">
            <select aria-label="Report type" value={type} onChange={(e) => setType(e.target.value as ReportType | '')} className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary">
              {types.map((item) => <option key={item} value={item}>{item ? item.replace(/_/g, ' ') : 'all types'}</option>)}
            </select>
            <select aria-label="Moderation status" value={status} onChange={(e) => setStatus(e.target.value as ReportModeration)} className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary">
              {statuses.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3"><Skeleton className="h-28 rounded-xl" /><Skeleton className="h-28 rounded-xl" /></div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-surface p-10 text-center text-text-secondary">No reports in this queue.</div>
        ) : (
          <ul className="space-y-3">
            {items.map((item) => (
              <li key={item.id} className="rounded-xl border border-border bg-surface p-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="font-semibold text-text-primary">{item.subject_key}</h2>
                      <span className="text-xs text-text-muted">{item.report_type.replace(/_/g, ' ')} · #{item.id}</span>
                    </div>
                    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                      {Object.entries(item.content).map(([key, value]) => (
                        <div key={key} className="contents">
                          <dt className="text-text-muted">{key}</dt>
                          <dd className="break-words text-text-primary">{String(value)}</dd>
                        </div>
                      ))}
                    </dl>
                    <div className="flex flex-wrap items-center gap-2">
                      <StalenessBadge staleness={item.staleness} ageHours={item.age_hours} />
                      <ConfidenceBadge confidence={item.confidence} corroborations={item.corroboration_count} />
                      <span className="text-xs text-text-muted">
                        source: {item.source_type}{item.source_channel ? ` (${item.source_channel})` : ''}
                        {item.reporter_id ? ` · reporter #${item.reporter_id}` : ''}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 lg:justify-end">
                    <button disabled={busy === item.id} onClick={() => moderate(item.id, 'approved')} className="rounded-lg bg-status-success px-3 py-2 text-sm font-medium text-white disabled:opacity-50">Approve</button>
                    <button disabled={busy === item.id} onClick={() => moderate(item.id, 'approved', true)} className="rounded-lg bg-brand-primary px-3 py-2 text-sm font-medium text-white disabled:opacity-50">Approve + verify</button>
                    <button disabled={busy === item.id} onClick={() => moderate(item.id, 'rejected')} className="rounded-lg bg-status-danger px-3 py-2 text-sm font-medium text-white disabled:opacity-50">Reject</button>
                    <button disabled={busy === item.id} onClick={() => moderate(item.id, 'hidden')} className="rounded-lg border border-border px-3 py-2 text-sm text-text-secondary disabled:opacity-50">Hide</button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </PageTransition>
  );
}
